// ─── PourquoiBloque.jsx ──────────────────────────────────────────────
// « Pourquoi je ne peux pas avancer ? »
//
// Un bouton, à chaque étape. Il ouvre la liste de ce qui manque, avec une
// action par ligne, et la liste de ce qui est déjà en place.
//
// Rien n'est généré : tout vient de `diagnostiquerBlocages`, calculé depuis
// l'état réel de la fiche. Un texte calculé est toujours juste, là où un
// texte deviné se trompe le jour où ça compte.
//
// Leçons de la revue ergo du 21.08 appliquées ici : rien sous 12 px, aucune
// écriture sombre sur fond sombre, et jamais un refus sans quoi faire.

import { useState } from 'react';
import { diagnostiquerBlocages } from '../services/blocagesParcours';

const VERT = '#1a2e1f';
const ENCRE = '#2e2c28';
const DOUX = '#5c6459';
const IVOIRE = '#fbfaf5';
const BORD = '#ddd8c8';
const AMBRE_FOND = '#fdf6e7';
const AMBRE_BORD = '#d9a441';
const AMBRE_ENCRE = '#5a3d08';

export default function PourquoiBloque({ client, journey, etape, onAller }) {
  const [ouvert, setOuvert] = useState(false);
  const { bloquants, acquis, couvert } = diagnostiquerBlocages(client, journey, etape);

  const libelleBouton = bloquants.length === 0
    ? 'Où j’en suis ?'
    : `Pourquoi je ne peux pas avancer ? (${bloquants.length})`;

  return (
    <div style={{ marginTop: 12 }}>
      <button
        type="button"
        onClick={() => setOuvert((v) => !v)}
        aria-expanded={ouvert}
        style={{
          border: `1px solid ${bloquants.length > 0 ? AMBRE_BORD : BORD}`,
          background: bloquants.length > 0 ? AMBRE_FOND : IVOIRE,
          color: bloquants.length > 0 ? AMBRE_ENCRE : ENCRE,
          borderRadius: 9, padding: '8px 13px', cursor: 'pointer',
          fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
        }}
      >
        {libelleBouton} {ouvert ? '▾' : '▸'}
      </button>

      {ouvert && (
        <div style={{
          border: `1px solid ${BORD}`, borderRadius: 12, background: IVOIRE,
          padding: '14px 16px', marginTop: 10,
        }}>
          {bloquants.length === 0 ? (
            <p style={{ fontSize: 13.5, color: ENCRE, margin: 0, lineHeight: 1.5 }}>
              <strong>Rien ne bloque à cette étape.</strong>
              {couvert
                ? ' Tout ce que le système sait vérifier ici est en place.'
                : ' Cette étape n’a pas encore de contrôle automatique : si tu es coincée, c’est ailleurs.'}
            </p>
          ) : (
            <>
              <p style={{ fontSize: 13.5, color: ENCRE, margin: '0 0 12px', lineHeight: 1.5 }}>
                <strong>
                  {bloquants.length === 1
                    ? 'Il manque une chose.'
                    : `Il manque ${bloquants.length} choses.`}
                </strong>
                {' '}Tant qu’elles ne sont pas réglées, l’étape suivante reste fermée.
              </p>

              {bloquants.map((p) => (
                <div
                  key={p.titre}
                  style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start',
                    padding: '10px 0', borderTop: `1px solid ${BORD}`,
                  }}
                >
                  <span aria-hidden="true" style={{
                    flex: '0 0 auto', width: 15, height: 15, marginTop: 2,
                    border: `1.5px solid ${AMBRE_BORD}`, borderRadius: 3,
                  }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: ENCRE, lineHeight: 1.4 }}>
                      {p.titre}
                    </div>
                    <div style={{ fontSize: 12.5, color: DOUX, marginTop: 3, lineHeight: 1.45 }}>
                      {p.quoiFaire}
                    </div>
                  </div>
                  {/* Le raccourci n'apparaît que si le parent sait y aller.
                      Un bouton qui ne mène nulle part vaut moins que rien. */}
                  {typeof onAller === 'function' && p.ou && (
                    <button
                      type="button"
                      onClick={() => onAller(p.ou)}
                      style={{
                        flex: '0 0 auto', border: `1px solid ${VERT}`, background: 'transparent',
                        color: VERT, borderRadius: 8, padding: '5px 11px', cursor: 'pointer',
                        fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit', whiteSpace: 'nowrap',
                      }}
                    >
                      Y aller
                    </button>
                  )}
                </div>
              ))}
            </>
          )}

          {acquis.length > 0 && (
            <div style={{ marginTop: 14, borderTop: `1px solid ${BORD}`, paddingTop: 10 }}>
              <p style={{ fontSize: 12.5, color: DOUX, margin: '0 0 6px' }}>Déjà en place</p>
              {acquis.map((p) => (
                <div key={p.titre} style={{ fontSize: 12.5, color: DOUX, lineHeight: 1.6 }}>
                  <span style={{ color: VERT, fontWeight: 700 }}>✓</span> {p.titre}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
